import * as Sentry from "@sentry/nextjs"
import { APIProvider } from "@today/api"
import { ErrorFallback } from "@today/ui/components/ErrorFallback"
import { AuthenticationProvider, AuthInitialProps, withAuth } from "@today/auth"
import axios from "axios"
import { BaseProvider, LightTheme, LocaleProvider } from "baseui"
import { AppContext, AppProps } from "next/app"
import Head from "next/head"
import { RecoilRoot } from "recoil"
import { Provider as StyletronProvider } from "styletron-react"
import { SWRConfig } from "swr"
import {
  NavBar as KioskNavBar,
  Container as KioskContainer,
} from "../components/kiosk"
import { RequiredNoticeModal } from "../components/modal/RequiredNoticeModal"
import {
  authConfig,
  INVOICE_BASE_URL,
  MAINTENANCE_MODE,
  TAKER_BASE_URL,
  TRACKER_BASE_URL,
} from "../config"
import { MaintenanceScreen } from "../components/maintenance/MaintenanceScreen"
import locale from "../locale"
import "../styles/globals.css"
import "../styles/kiosk.css"
import { styletron } from "../styletron"
import { LoadingOverlay } from "../components/common/LoadingOverlay"
import { useRouter } from "next/router"
import { SessionProvider } from "next-auth/react"
import { useEffect } from "react"
import * as gtag from "../lib/gtag"
import Script from "next/script"
import { PointsProvider } from "../providers/PointsProvider"
import { ToasterContainer } from "baseui/toast"
import DevNavigation from "../components/dev-navigation/DevNavigation"
import SideBar from "../components/navigation/SideBar"
import { NotificationDropdown } from "../components/notifications/NotificationDropdown"
import { useUserInfo } from "@today/auth"
import { ROLE } from "../utils/role"
import { PropsWithChildren } from "react"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import timezone from "dayjs/plugin/timezone"
import isSameOrAfter from "dayjs/plugin/isSameOrAfter"
import relativeTime from "dayjs/plugin/relativeTime"
import "dayjs/locale/ko"

dayjs.locale("ko")
dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(isSameOrAfter)
dayjs.extend(relativeTime)

function MainContent({ children }: PropsWithChildren<{}>) {
  const { clientRoles } = useUserInfo()
  const hasCSTicketRole = clientRoles.includes(ROLE.csTicket)

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {hasCSTicketRole && (
        <div className="flex items-center justify-end border-b border-gray-100 px-4 py-2">
          <NotificationDropdown />
        </div>
      )}
      <div className="flex-1 overflow-hidden">{children}</div>
    </div>
  )
}

function App({
  Component,
  pageProps,
  authInitialProp,
}: AppProps & AuthInitialProps) {
  const router = useRouter()

  useEffect(() => {
    const handleRouteChange = (url: URL) => {
      gtag.pageview(url)
    }
    router.events.on("routeChangeComplete", handleRouteChange)
    return () => {
      router.events.off("routeChangeComplete", handleRouteChange)
    }
  }, [router.events])

  // 점검 모드: 웹/키오스크 분기 및 인증 Provider 진입 전에 전 경로를 차단한다.
  if (MAINTENANCE_MODE) {
    return <MaintenanceScreen />
  }

  // 키오스크 페이지
  if (router.route.startsWith("/kiosk")) {
    return (
      <>
        <Head>
          <title>Today 파트너</title>
          <meta name="google" content="notranslate" />
        </Head>
        <RecoilRoot>
          {/* 키오스크는 Keycloak 세션을 쓰지 않지만, 공유 컴포넌트(IncheonTemporaryGuard,
              RequiredNoticeModal 등)가 useUserInfo()→useSession() 을 호출하므로 SessionProvider 가
              필요하다. 세션을 null 로 고정하고 백그라운드 폴링을 꺼 미인증 기본값을 반환한다. */}
          <SessionProvider
            session={null}
            refetchInterval={0}
            refetchOnWindowFocus={false}
          >
            <APIProvider
              takerBaseUrl={TAKER_BASE_URL}
              invoiceBaseUrl={INVOICE_BASE_URL}
            >
              <StyletronProvider value={styletron}>
                <BaseProvider theme={LightTheme}>
                  <LocaleProvider locale={locale}>
                    <KioskContainer>
                      <KioskNavBar />
                      <div
                        className="flex-1 overflow-hidden"
                        style={{
                          wordBreak: "keep-all",
                        }}
                      >
                        <Sentry.ErrorBoundary
                          fallback={({ eventId }) => (
                            <ErrorFallback eventId={eventId} />
                          )}
                        >
                          <Component {...pageProps} />
                        </Sentry.ErrorBoundary>
                      </div>
                    </KioskContainer>
                    <RequiredNoticeModal />
                  </LocaleProvider>
                </BaseProvider>
              </StyletronProvider>
            </APIProvider>
          </SessionProvider>
        </RecoilRoot>
      </>
    )
  }
  // 파트너 페이지
  return (
    <>
      <Head>
        <title>Today 파트너</title>
        <meta name="google" content="notranslate" />
        <script
          dangerouslySetInnerHTML={{
            __html: `
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', '${gtag.trackingId}', {
                page_path: window.location.pathname,
              });
            `,
          }}
        />
      </Head>
      <Script
        strategy="afterInteractive"
        src={`https://www.googletagmanager.com/gtag/js?id=${gtag.trackingId}`}
      />
      <RecoilRoot>
        <AuthenticationProvider
          {...authConfig}
          authInitialProp={authInitialProp}
          allowAnonymousPaths={{
            "/account": "prefix",
          }}
          loadingOverlay={<LoadingOverlay enableSessionReset />}
        >
          <APIProvider
            takerBaseUrl={TAKER_BASE_URL}
            trackerBaseUrl={TRACKER_BASE_URL}
            invoiceBaseUrl={INVOICE_BASE_URL}
          >
            <SWRConfig
              value={{
                fetcher: (url) => axios.get(url).then((res) => res.data),
                onError: (error, key) => {
                  const isAxios = axios.isAxiosError(error)
                  const status = isAxios ? error.response?.status : undefined
                  if (!isAxios || (status !== undefined && status >= 500)) {
                    Sentry.captureException(error, {
                      tags: { errorSource: "swr" },
                      extra: { swrKey: key },
                    })
                  }
                },
              }}
            >
              <StyletronProvider value={styletron}>
                <BaseProvider theme={LightTheme}>
                  <LocaleProvider locale={locale}>
                    <ToasterContainer
                      placement="bottomLeft"
                      autoHideDuration={3000}
                    >
                      <PointsProvider>
                        <div
                          className="flex h-screen flex-col"
                          style={{ height: "100dvh" }}
                        >
                          <DevNavigation />
                          <div className="flex flex-1 overflow-hidden">
                            <SideBar />
                            <MainContent>
                              <Sentry.ErrorBoundary
                                fallback={({ eventId }) => (
                                  <ErrorFallback eventId={eventId} />
                                )}
                              >
                                <Component {...pageProps} />
                              </Sentry.ErrorBoundary>
                            </MainContent>
                          </div>
                        </div>
                        <RequiredNoticeModal />
                      </PointsProvider>
                    </ToasterContainer>
                  </LocaleProvider>
                </BaseProvider>
              </StyletronProvider>
            </SWRConfig>
          </APIProvider>
        </AuthenticationProvider>
      </RecoilRoot>
    </>
  )
}

App.getInitialProps = withAuth(async (context: AppContext) => ({}))

export default App
